import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import color from "color";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import { Territory } from "@mapmycustomers/shared/types/entity";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import useBoundsContext from "@app/scene/map/components/TerritoriesMode/utils/useBoundsContext";
import { getTerritory } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import getContrastColor from "@app/util/colors/getContrastColor";
import useAnalytics from "@app/util/contexts/useAnalytics";

import { TERRITORY_BANNER_CLASS_NAME } from "../../utils/consts";

import styles from "./CommonBanner.module.scss";

interface Props {
  onCancel?: () => void;
  territory?: Territory;
}

const ViewBanner: React.FC<Props> = ({ onCancel, territory }) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const { color: territoryColor } = useBoundsContext();

  const [backgroundColor, textColor] = useMemo(() => {
    const background = color(anyColorToHex(territoryColor || Color.BLACK))
      .lightness(80)
      .hex();
    const textColor = getContrastColor(background) === "white" ? colors.white : colors.slate;
    return [background, textColor];
  }, [territoryColor]);

  const handleExit = useCallback(() => {
    onCancel?.();
    analytics.completed("");
  }, [analytics, onCancel]);

  return territory ? (
    <div className={cn(styles.container, TERRITORY_BANNER_CLASS_NAME)} style={{ backgroundColor }}>
      <div className={styles.title} style={{ color: textColor }}>
        {intl.formatMessage(
          {
            id: "map.territory.topPanel.banner.title",
            defaultMessage: "You are viewing the Territory {territoryName}",
            description: "Territory mode banner's title on the Map page",
          },
          {
            territoryName: territory ? (
              intl.formatMessage(
                {
                  id: "map.territory.topPanel.banner.title.name",
                  defaultMessage: "“{territoryName}”",
                  description: "Territory name in territory mode banner's title on the Map page",
                },
                { territoryName: territory.name }
              )
            ) : (
              <LoadingSpinner className={styles.spinner} mini />
            ),
          }
        )}
      </div>
      <div className={styles.actions}>
        <Interactive>
          <Button onClick={handleExit} type="default">
            {intl.formatMessage({
              id: "map.territory.topPanel.banner.exit",
              defaultMessage: "Exit Territory View",
              description: "Territory mode banner's exit button on the Map page",
            })}
          </Button>
        </Interactive>
      </div>
    </div>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  territory: getTerritory(state),
});

export default connect(mapStateToProps)(ViewBanner);
